import type { ViolationReportContextTypeApi } from 'api-typings/users/users';
import {
  ViolationReportAdditionalReasonApi,
  ViolationReportReasonApi,
} from 'api-typings/users/users';

export const violationReportReasonText = {
  COPIED_WORK_FROM_USER: $localize`They copied my work`,
  COPIED_WORK_FROM_SOMEONE_ELSE: $localize`They copied work from another source`,
  WORK_DOES_NOT_MATCH_REQUIREMENTS: $localize`Work does not match requirements`,
  EXPLICIT_OR_INAPPROPRIATE_CONTENT: $localize`Submitted explicit or inappropriate content`,
  LOW_QUALITY_WORK: $localize`Low-quality work`,
  PUBLIC_DISPLAY_OF_COMMUNICATION_DETAILS: $localize`They are publicly displaying their contact information`,
  INAPPROPRIATE_LANGUAGE: $localize`They have been using inappropriate language`,
  DOES_NOT_MATCH_PROJECT_DESCRIPTION: $localize`Bidder did not read project description`,
  DOES_NOT_PROVIDE_ENOUGH_INFORMATION: $localize`Unclear or does not provide enough information`,
  CONTAINS_CONTACT_INFORMATION: $localize`Contains contact information`,
  ADVERTISING_ANOTHER_WEBSITE: $localize`Advertising another website`,
  FAKE_INFORMATION_POSTED: $localize`Fake information posted`,
  FAKE_PROJECT_POSTED: $localize`Fake project posted`,
  OBSCENITIES_OR_HARASSING_BEHAVIOUR: $localize`Obscenities or harassing behaviour`,
  NON_FULLTIME_PROJECT: $localize`Non-full time project posted requiring abnormal bidding`,
  POSTING_CONTACT_INFORMATION: $localize`Posting contact information`,
  HARMFUL_LANGUAGE: $localize`Harmful language`,
  IRRELEVANT_INFORMATION: $localize`Irrelevant to both my project and ideal freelancer`,
  NOT_MAKING_SENSE: $localize`Does not make sense`,
  EXCESSIVE_SELF_PROMOTION: $localize`Excessive self promotion`,
  OTHER: $localize`Other`,

  // Loadshift specific reasons
  LOAD_IS_NOT_AVAILABLE: `Load is not available`,
  INACCURATE_LOAD_DETAILS: `Inaccurate load details`,
  UNRESPONSIVE_SHIPPER: `Unresponsive shipper`,
  SHIPPER_NEEDS_HELP_USING_PLATFORM: `Shipper needs help using platform`,
  CARRIER_POSTING_TRUCK_AVAILABILITY: `Carrier posting truck availability`,
  POTENTIAL_COR_CONCERN: `Potential CoR concern`,
  OFF_PLATFORM_PAYMENT: `Potential off-platform payment`,
};

export enum ReportStep {
  REASON = 1,
  DESCRIPTION = 2,
  SUCCESS = 3,
}

export interface ViolationReportDetails {
  readonly violatorUserId: number;
  readonly contextId: number;
  readonly contextType: ViolationReportContextTypeApi;
}

interface ViolationReasonMapping {
  readonly [key: string]: {
    readonly reason: ViolationReportReasonApi;
    readonly additionalReason: ViolationReportAdditionalReasonApi;
  };
}

export const VIOLATION_REASON_TEXT_MAPPING: ViolationReasonMapping = {
  [violationReportReasonText.OTHER]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.PUBLIC_DISPLAY_OF_COMMUNICATION_DETAILS]: {
    reason: ViolationReportReasonApi.CONTACTS,
    additionalReason:
      ViolationReportAdditionalReasonApi.PUBLIC_DISPLAY_OF_COMMUNICATION_DETAILS,
  },
  [violationReportReasonText.EXPLICIT_OR_INAPPROPRIATE_CONTENT]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason:
      ViolationReportAdditionalReasonApi.EXPLICIT_OR_INAPPROPRIATE_CONTENT,
  },
  [violationReportReasonText.COPIED_WORK_FROM_USER]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.COPIED_WORK_FROM_USER,
  },
  [violationReportReasonText.COPIED_WORK_FROM_SOMEONE_ELSE]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason:
      ViolationReportAdditionalReasonApi.COPIED_WORK_FROM_SOMEONE_ELSE,
  },
  [violationReportReasonText.WORK_DOES_NOT_MATCH_REQUIREMENTS]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason:
      ViolationReportAdditionalReasonApi.WORK_DOES_NOT_MATCH_REQUIREMENTS,
  },
  [violationReportReasonText.LOW_QUALITY_WORK]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.LOW_QUALITY_WORK,
  },
  [violationReportReasonText.INAPPROPRIATE_LANGUAGE]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.INAPPROPRIATE_LANGUAGE,
  },
  [violationReportReasonText.DOES_NOT_MATCH_PROJECT_DESCRIPTION]: {
    reason: ViolationReportReasonApi.FAKE,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.DOES_NOT_PROVIDE_ENOUGH_INFORMATION]: {
    reason: ViolationReportReasonApi.FAKE,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.CONTAINS_CONTACT_INFORMATION]: {
    reason: ViolationReportReasonApi.CONTACTS,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.ADVERTISING_ANOTHER_WEBSITE]: {
    reason: ViolationReportReasonApi.ADVERTISING,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.FAKE_INFORMATION_POSTED]: {
    reason: ViolationReportReasonApi.FAKE,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.FAKE_PROJECT_POSTED]: {
    reason: ViolationReportReasonApi.FAKE,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.OBSCENITIES_OR_HARASSING_BEHAVIOUR]: {
    reason: ViolationReportReasonApi.HARASSMENT,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.NON_FULLTIME_PROJECT]: {
    reason: ViolationReportReasonApi.NONFEATURED,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.POSTING_CONTACT_INFORMATION]: {
    reason: ViolationReportReasonApi.CONTACTS,
    additionalReason: ViolationReportAdditionalReasonApi.OTHER,
  },
  [violationReportReasonText.HARMFUL_LANGUAGE]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.INAPPROPRIATE_LANGUAGE,
  },
  [violationReportReasonText.IRRELEVANT_INFORMATION]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.IRRELEVANT_INFORMATION,
  },
  [violationReportReasonText.NOT_MAKING_SENSE]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.NOT_MAKING_SENSE,
  },
  [violationReportReasonText.EXCESSIVE_SELF_PROMOTION]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason:
      ViolationReportAdditionalReasonApi.EXCESSIVE_SELF_PROMOTION,
  },
  [violationReportReasonText.LOAD_IS_NOT_AVAILABLE]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.PROJECT_UNAVAILABLE,
  },
  [violationReportReasonText.INACCURATE_LOAD_DETAILS]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason:
      ViolationReportAdditionalReasonApi.INACCURATE_PROJECT_DETAILS,
  },
  [violationReportReasonText.UNRESPONSIVE_SHIPPER]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.UNRESPONSIVE_USER,
  },
  [violationReportReasonText.SHIPPER_NEEDS_HELP_USING_PLATFORM]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.USER_NEEDS_ASSISTANCE,
  },
  [violationReportReasonText.CARRIER_POSTING_TRUCK_AVAILABILITY]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason:
      ViolationReportAdditionalReasonApi.USER_AVAILABILITY_POSTED_AS_PROJECT,
  },
  [violationReportReasonText.POTENTIAL_COR_CONCERN]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason:
      ViolationReportAdditionalReasonApi.CHAIN_OF_RESPONSIBILITY_CONCERN,
  },
  [violationReportReasonText.OFF_PLATFORM_PAYMENT]: {
    reason: ViolationReportReasonApi.OTHER,
    additionalReason: ViolationReportAdditionalReasonApi.OFFSITING_PAYMENT,
  },
};
